import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, LayoutGrid, FileText, ShoppingCart, Bell, User, LogOut,
  CalendarClock, Wrench, TrendingUp, Boxes, Wallet, Sun, Moon, Monitor,
  ChevronDown, Calendar, Menu as MenuIcon, X, Undo2,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { brand } from '../assets/brand';
import { onForegroundMessage, isFcmConfigured } from '../lib/fcm';
import { ChatbotWidget } from './ChatbotWidget';
import { BackButton } from './BackButton';
import { Seo } from '../lib/seo';
import { NotificationBell } from './NotificationBell';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthRefresh } from '../hooks/useAuthRefresh';
import { useBadges } from '../hooks/useBadges';
import { FULFILLMENT_V2_ENABLED, RETURNS_LIFECYCLE_ENABLED } from '../lib/flags';
import {
  Avatar, CountBadge, Dropdown, IconButton, MenuDivider, MenuItem, MenuLabel, Tooltip, toast, cn,
} from '../ui';
import { useGsapPageEnter } from '../ui/motion';

// Routes that are a "home"/landing — no Back button there (nothing meaningful to go back to).
const HIDE_BACK = new Set(['/', '/home', '/dashboard']);

/* ───────────────────────── Navigation model ─────────────────────────────
 * Mirrors the original AppShell exactly: each leaf carries the permission
 * code(s) that gate it and (optionally) a `roles` restriction; a group is
 * hidden when none of its children are visible. SUPER_ADMIN (wildcard) sees
 * everything. Only the rendering changed — the gating/badge logic is identical.
 * --------------------------------------------------------------------------*/
type NavLeaf = {
  key: string;
  label: string;
  to: string;
  icon?: ReactNode;
  badge?: number;
  perm?: string | string[];
  roles?: string[];
};
type NavDivider = { divider: true };
type NavChild = NavLeaf | NavDivider;
type NavGroup = {
  key: string;
  label: string;
  icon?: ReactNode;
  to?: string;
  perm?: string | string[];
  roles?: string[];
  badge?: number;
  children?: NavChild[];
};

const isDivider = (c: NavChild): c is NavDivider => 'divider' in c;

export function AppShellModern() {
  useAuthRefresh(); // keep role/permissions live without a full re-login
  const badges = useBadges(); // live "active work" counts (polled), permission-gated server-side
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const isCustomer = useAuthStore((s) => s.isCustomer)();
  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const isDark = useThemeStore((s) => s.resolved)() === 'dark';

  const queryClient = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Close the mobile menu whenever the route changes.
  useEffect(() => setMobileOpen(false), [location.pathname]);
  // Replays a subtle entrance on the content region each navigation.
  const pageRef = useGsapPageEnter<HTMLDivElement>([location.pathname]);

  // FCM foreground toast — kept on antd's imperative notification API (invisible
  // chrome, not a styling concern; a Tailwind toast system is a later step).
  useEffect(() => {
    if (!isFcmConfigured()) return;
    let dispose: (() => void) | undefined;
    onForegroundMessage((payload) => {
      const n = payload?.notification ?? {};
      toast(n.title ?? 'Green Bay Bio', { description: n.body ?? '' });
      // Refresh the bell feed immediately so the new notification + unread count appear.
      queryClient.invalidateQueries({ queryKey: ['notifications-feed'] });
    }).then((unsub) => { dispose = unsub; });
    return () => { dispose?.(); };
  }, [queryClient]);

  // ───────── Menu data (identical routes / perms / roles / badges) ─────────
  const STAFF_MENU: NavGroup[] = [
    {
      key: '/dashboard', icon: <LayoutDashboard />, label: 'Dashboard', to: '/dashboard',
      children: [
        { key: '/dashboard', label: 'Executive & Sales', to: '/dashboard', perm: ['DASH.EXECUTIVE', 'DASH.SALES'] },
        { key: '/dashboard/map', label: 'Map', to: '/dashboard/map', perm: 'DASH.MAP' },
        { key: '/people', label: 'Users', to: '/people', perm: ['RETAILER.VIEW', 'FARMER.VIEW', 'USER.VIEW'] },
      ],
    },
    {
      key: 'masters', icon: <LayoutGrid />, label: 'Masters',
      children: [
        { key: '/products', label: 'Products', to: '/products', perm: 'PRODUCT.VIEW' },
        { divider: true },
        // Territories: Structure (hierarchy CRUD) + Coverage (assignments) are tabs of one page; badge = coverage gaps.
        { key: '/masters/territories', label: 'Territories', to: '/masters/territories', badge: (badges.territoriesUnassigned ?? 0) + (badges.coverageNeeded ?? 0), perm: ['TERRITORY.VIEW', 'TERRITORY.ASSIGN', 'TERRITORY.ASSIGN.HEAD', 'TERRITORY.COVERAGE'] },
        { key: '/masters/tax', label: 'Tax (HSN)', to: '/masters/tax', perm: 'TAX_MASTER.VIEW' },
        { key: '/masters/commercial-terms', label: 'Commercial Terms', to: '/masters/commercial-terms', perm: 'COMMERCIAL.VIEW' },
      ],
    },
    {
      key: 'inv', icon: <Boxes />, label: 'Warehouse',
      children: [
        { key: '/inventory/warehouses', label: 'Warehouses', to: '/inventory/warehouses', perm: 'WAREHOUSE.VIEW' },
        // Master Stock is now the stock-operations hub (Master Stock + Transfers + Adjustments tabs).
        // Perm = union of the three features so transfer/adjust-only users still reach it; its badge
        // sums all the warehouse pending work (awaiting dispatch + receipt + adjustment approvals).
        { key: '/inventory/master-stock', label: 'Master Stock', to: '/inventory/master-stock', badge: (badges.transfersAwaitingReceipt ?? 0) + (badges.transfersAwaitingDispatch ?? 0) + (badges.adjustmentsPendingApproval ?? 0), perm: ['STOCK.MASTER.ADD', 'STOCK.VIEW', 'STOCK.TRANSFER', 'STOCK.ADJUST.CREATE', 'STOCK.ADJUST.APPROVE'] },
        { key: '/inventory/stock', label: 'Stock & Valuation', to: '/inventory/stock', perm: 'STOCK.VIEW' },
        // Outbound dispatch register — WHM sees only their warehouse(s), Admin/SA see all (with a warehouse filter).
        { key: '/warehouse/dispatches', label: 'Dispatches', to: '/warehouse/dispatches', perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'] },
        // Non-sellable stock (expired · QC-hold · blocked · past-expiry) now lives as a tab on Stock & Valuation.
        // Admins reach Activity via the tab on the Warehouses page; a Warehouse Manager (no access to the
        // Warehouses page) keeps this standalone, warehouse-scoped entry.
        { key: '/warehouse/activity', label: 'Activity', to: '/warehouse/activity', perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'], roles: ['WAREHOUSE_MANAGER'] },
        // Admins reach Low Stock via the tab on the Warehouses page; a Warehouse Manager (who can't open
        // the Warehouses page) gets this standalone, warehouse-scoped entry.
        { key: '/inventory/low-stock', label: 'Low Stock', to: '/inventory/low-stock', perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'], roles: ['WAREHOUSE_MANAGER'] },
        { key: '/inventory/grn', label: 'GRN', to: '/inventory/grn', perm: 'GRN.VIEW' },
        { key: '/inventory/batch-trace', label: 'Batch Traceability', to: '/inventory/batch-trace', perm: 'STOCK.VALUATION.VIEW' },
      ],
    },
    {
      key: 'sales', icon: <ShoppingCart />, label: 'Sales',
      children: [
        { key: '/orders', label: 'Orders', to: '/orders', badge: badges.ordersActive, perm: 'ORDER.VIEW' },
        { key: '/orders/new', label: 'New Order', to: '/orders/new', perm: 'ORDER.CREATE' },
        { key: '/orders/approval', label: 'Approval Queue', to: '/orders/approval', badge: badges.ordersPendingApproval, perm: 'ORDER.APPROVE' },
        // Fulfillment V2 · Phase 2.5 — persisted shortfall queue (planners resolve; a WHM sees their own, read-only).
        ...(FULFILLMENT_V2_ENABLED ? [{ key: '/orders/shortfalls', label: 'Shortfalls', to: '/orders/shortfalls', badge: badges.shortfallsOpen, perm: ['ORDER.DISPATCH.PLAN', 'ORDER.EDIT', 'WAREHOUSE.PACK.START', 'WAREHOUSE.PACK.COMPLETE'] }] : []),
        { key: '/inquiries', label: 'Inquiries', to: '/inquiries', badge: badges.openInquiries, perm: 'INQUIRY.VIEW' },
      ],
    },
    {
      key: 'billing', icon: <FileText />, label: 'Billing',
      children: [
        { key: '/billing/invoices', label: 'Invoices', to: '/billing/invoices', badge: badges.openInvoices, perm: 'INVOICE.VIEW' },
        ...(RETURNS_LIFECYCLE_ENABLED ? [{ key: '/returns', label: 'Returns', to: '/returns', badge: badges.returnsActionable, perm: 'RETURN.VIEW' }] : []),
        { key: '/billing/payments', label: 'Payments', to: '/billing/payments', badge: badges.paymentsPending, perm: 'PAYMENT.VIEW' },
        { key: '/billing/credit-notes', label: 'Credit Notes', to: '/billing/credit-notes', perm: 'INVOICE.VIEW' },
        { key: '/billing/aging', label: 'Aging', to: '/billing/aging', perm: 'OUTSTANDING.VIEW' },
      ],
    },
    {
      key: 'ffa', icon: <CalendarClock />, label: 'Field Force',
      children: [
        { key: '/ffa/my-day', label: 'My Field Day', to: '/ffa/my-day', perm: 'ATTENDANCE.SELF', roles: ['SALES_PERSON', 'SALES_HEAD'] },
        { key: '/ffa/attendance', label: 'Attendance', to: '/ffa/attendance', badge: (badges.attendanceAnomalies ?? 0) + (badges.regularizationsPending ?? 0) + (badges.attendanceIncomplete ?? 0), perm: 'ATTENDANCE.VIEW' },
        { key: '/ffa/visits', label: 'Visits', to: '/ffa/visits', perm: 'VISIT.VIEW' },
        { key: '/ffa/feedback', label: 'Feedback', to: '/ffa/feedback', perm: 'FEEDBACK.VIEW' },
        { key: '/events', label: 'Events & Meets', to: '/events', badge: badges.upcomingEvents, perm: 'EVENT.VIEW' },
        { divider: true },
        // Moved here from Dashboard / Billing: sales-person performance + their advances & expense claims.
        { key: '/performance', label: 'Sales Performance', to: '/performance', perm: 'DASH.SALES' },
        { key: '/wallets', label: 'Sales Person Advances', to: '/wallets', badge: badges.advanceRequestsAwaitingMe || badges.myAdvanceRequestsPending, perm: 'EXPENSE.VIEW' },
        { key: '/expense-reports', label: 'Expense Reports', to: '/expense-reports', badge: badges.expenseAwaitingMe, perm: 'EXPENSE.VIEW' },
      ],
    },
    { key: '/complaints', icon: <Wrench />, label: 'Complaints', to: '/complaints', badge: badges.openComplaints, perm: 'COMPLAINT.VIEW' },
    {
      key: 'more', icon: <TrendingUp />, label: 'More',
      children: [
        { key: '/reports', label: 'Reports', to: '/reports', perm: 'REPORT.RUN' },
        { key: '/notifications/templates', label: 'Templates (FCM/SMS/Email)', to: '/notifications/templates', perm: 'NOTIF.TEMPLATE.ADMIN' },
        { key: '/notifications/wa-templates', label: 'WhatsApp Templates', to: '/notifications/wa-templates', perm: 'NOTIF.TEMPLATE.ADMIN' },
        { divider: true },
        { key: '/admin/banners', label: 'Landing Banners', to: '/admin/banners', perm: 'BANNER.MANAGE' },
        { key: '/admin/bank-details', label: 'Bank Details', to: '/admin/bank-details', perm: 'COMPANY.BANK.EDIT' },
        { key: '/admin/roles', label: 'Roles & Permissions', to: '/admin/roles', perm: 'ROLE.VIEW' },
        { key: '/admin/activity', label: 'Activity Log', to: '/admin/activity', perm: 'ACTIVITY.VIEW' },
        { key: '/admin/app-installs', label: 'App Installs', to: '/admin/app-installs', perm: 'APP_INSTALL.VIEW' },
      ],
    },
  ];

  const CUSTOMER_MENU: NavGroup[] = [
    { key: '/orders', icon: <ShoppingCart />, label: 'My Orders', to: '/orders', badge: badges.ordersActive, perm: 'ORDER.VIEW' },
    { key: '/billing/invoices', icon: <FileText />, label: 'Invoices', to: '/billing/invoices', badge: badges.openInvoices, perm: 'INVOICE.VIEW' },
    ...(RETURNS_LIFECYCLE_ENABLED ? [{ key: '/returns', icon: <Undo2 />, label: 'Returns', to: '/returns', badge: badges.returnsActionable, perm: 'RETURN.VIEW' }] : []),
    { key: '/account', icon: <Wallet />, label: 'My Account', to: '/account', perm: 'OUTSTANDING.VIEW', roles: ['RETAILER'] },
    { key: '/events', icon: <Calendar />, label: 'Events', to: '/events', badge: badges.upcomingEvents, perm: 'EVENT.VIEW' },
    { key: '/complaints', icon: <Wrench />, label: 'Complaints', to: '/complaints', badge: badges.openComplaints, perm: 'COMPLAINT.VIEW' },
    { key: '/profile', icon: <User />, label: 'Profile', to: '/profile' },
  ];

  // ───────── Visibility + pruning (ported verbatim) ─────────
  const hasPerm = (perm?: string | string[]) =>
    hasAnyPermission(perm ? (Array.isArray(perm) ? perm : [perm]) : undefined);
  const myRoles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
  const roleOk = (roles?: string[]) => !roles || roles.some((r) => myRoles.includes(r));
  const leafVisible = (l: NavLeaf) => roleOk(l.roles) && hasPerm(l.perm);
  const groupSelfVisible = (g: NavGroup) => roleOk(g.roles) && hasPerm(g.perm);

  const pruneChildren = (children: NavChild[]): NavChild[] => {
    const out: NavChild[] = [];
    for (const c of children) {
      if (isDivider(c)) {
        if (out.length === 0 || isDivider(out[out.length - 1])) continue;
        out.push({ divider: true });
      } else if (leafVisible(c)) {
        out.push(c);
      }
    }
    while (out.length && isDivider(out[out.length - 1])) out.pop();
    return out;
  };

  const nav = isCustomer ? CUSTOMER_MENU : STAFF_MENU;

  // ───────── Active-key resolution (longest matching route prefix) ─────────
  const collectRoutes = (groups: NavGroup[]): string[] =>
    groups.flatMap((g) => [
      ...(g.to ? [g.to] : []),
      ...(g.children?.filter((c): c is NavLeaf => !isDivider(c)).map((c) => c.to) ?? []),
    ]);
  const activeKey = (() => {
    const p = location.pathname;
    const keys = collectRoutes(nav).filter((k) => k.startsWith('/'));
    let best = '';
    for (const k of keys) if ((p === k || p.startsWith(k + '/')) && k.length > best.length) best = k;
    if (!best && !isCustomer && (p.startsWith('/retailers') || p.startsWith('/farmers'))) return '/people';
    return best || (isCustomer ? '/orders' : '/dashboard');
  })();

  const groupRoutes = (g: NavGroup): string[] => [
    ...(g.to ? [g.to] : []),
    ...(g.children?.filter((c): c is NavLeaf => !isDivider(c)).map((c) => c.to) ?? []),
  ];
  const isGroupActive = (g: NavGroup) => groupRoutes(g).includes(activeKey);

  // ───────── Pill class helpers ─────────
  const pillBase =
    'inline-flex h-9 items-center gap-1.5 rounded-control px-3 text-sm font-medium transition-colors duration-150 whitespace-nowrap';
  // Note: nav items that have a route render as <a>, and Ant Design's global
  // anchor color would override these — so the text colors are forced (!) to win.
  const pillResting = 'text-content/70! hover:bg-brand-50 hover:text-brand-700! dark:hover:bg-brand-900/25';
  const pillActive = 'bg-brand-50 text-brand-700! font-semibold dark:bg-brand-900/30 dark:text-brand-300!';

  // Renders the desktop pill (Link if it has a route, else a button trigger).
  const renderPill = (g: NavGroup, opts: { active: boolean; total?: number; caret?: boolean }) => {
    const content = (
      <>
        <span>{g.label}</span>
        {!!opts.total && <CountBadge count={opts.total} className="ml-0.5 ring-0" />}
        {opts.caret && <ChevronDown size={14} className="opacity-60" />}
      </>
    );
    const cls = cn(pillBase, opts.active ? pillActive : pillResting);
    return g.to ? (
      <Link to={g.to} className={cls}>{content}</Link>
    ) : (
      <button type="button" className={cls}>{content}</button>
    );
  };

  // One top-level nav entry (desktop).
  const renderNavGroup = (g: NavGroup): ReactNode => {
    if (!g.children) {
      if (!groupSelfVisible(g)) return null;
      return <div key={g.key}>{renderPill(g, { active: isGroupActive(g), total: g.badge })}</div>;
    }
    const kids = pruneChildren(g.children);
    if (!kids.length) return null;
    const total = kids.reduce((s, c) => s + (isDivider(c) ? 0 : c.badge ?? 0), 0);
    const active = isGroupActive(g);
    return (
      <Dropdown
        key={g.key}
        trigger="hover"
        panel={kids.map((c, i) =>
          isDivider(c) ? (
            <MenuDivider key={`d${i}`} />
          ) : (
            <MenuItem
              key={c.key}
              to={c.to}
              active={c.to === activeKey}
              icon={c.icon}
              trailing={<CountBadge count={c.badge} />}
            >
              {c.label}
            </MenuItem>
          ),
        )}
      >
        {renderPill(g, { active, total, caret: true })}
      </Dropdown>
    );
  };

  // Vertical version for the mobile sheet.
  const renderMobileGroup = (g: NavGroup): ReactNode => {
    if (!g.children) {
      if (!groupSelfVisible(g)) return null;
      return (
        <MenuItem key={g.key} to={g.to} icon={g.icon} active={isGroupActive(g)} trailing={<CountBadge count={g.badge} />}>
          {g.label}
        </MenuItem>
      );
    }
    const kids = pruneChildren(g.children);
    if (!kids.length) return null;
    return (
      <div key={g.key} className="py-1">
        <MenuLabel>{g.label}</MenuLabel>
        {kids.map((c, i) =>
          isDivider(c) ? (
            <MenuDivider key={`d${i}`} />
          ) : (
            <MenuItem key={c.key} to={c.to} active={c.to === activeKey} trailing={<CountBadge count={c.badge} />}>
              {c.label}
            </MenuItem>
          ),
        )}
      </div>
    );
  };

  const themeOptions: { mode: 'light' | 'dark' | 'system'; label: string; icon: ReactNode }[] = [
    { mode: 'light', label: 'Light', icon: <Sun /> },
    { mode: 'dark', label: 'Dark', icon: <Moon /> },
    { mode: 'system', label: 'Follow system', icon: <Monitor /> },
  ];

  const headerIconBtn =
    'border border-line text-content/70 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20';

  return (
    <div className="flex min-h-screen flex-col">
      {/* The entire authenticated workspace is kept out of search engines. */}
      <Seo noindex title="Workspace" description="Green Bay Biotech internal workspace." />
      {/* ───────────────── Header — single full-width bar with a thin brand accent ───────────────── */}
      <header className="sticky top-0 z-[100] border-t-2 border-brand-600 border-b border-line bg-surface shadow-soft">
        <div className="flex h-16 items-center gap-4 px-4 sm:px-6">
          {/* Brand */}
          <Link to="/" className="flex shrink-0 items-center">
            <img src="/logo.png" alt="Green Bay Biotech International" className="h-9 w-auto sm:h-11" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden min-w-0 flex-1 items-center gap-0.5 xl:flex">
            {nav.map(renderNavGroup)}
          </nav>

          {/* spacer (mobile pushes controls right) */}
          <div className="flex-1 xl:hidden" />

          {/* Right controls */}
          <div className="flex shrink-0 items-center gap-2">
            <Tooltip label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton aria-label="Toggle theme" className={headerIconBtn} onClick={toggleTheme}>
                {isDark ? <Sun /> : <Moon />}
              </IconButton>
            </Tooltip>
            <NotificationBell />

            {/* User menu */}
            <Dropdown
              trigger="click"
              align="end"
              panelClassName="min-w-[230px]"
              panel={
                <>
                  <MenuLabel>{user?.role}</MenuLabel>
                  <MenuDivider />
                  <MenuItem icon={<User />} onClick={() => navigate('/profile')}>My profile</MenuItem>
                  <MenuItem icon={<Bell />} onClick={() => navigate('/profile/notifications')}>
                    Notification preferences
                  </MenuItem>
                  <MenuDivider />
                  <MenuLabel>Theme</MenuLabel>
                  {themeOptions.map((t) => (
                    <MenuItem
                      key={t.mode}
                      icon={t.icon}
                      active={themeMode === t.mode}
                      onClick={() => setThemeMode(t.mode)}
                    >
                      {t.label}
                    </MenuItem>
                  ))}
                  <MenuDivider />
                  <MenuItem
                    icon={<LogOut />}
                    danger
                    onClick={() => { logout(); navigate('/'); }}
                  >
                    Logout
                  </MenuItem>
                </>
              }
            >
              <button
                type="button"
                className="flex items-center gap-2.5 rounded-pill border border-line py-1 pl-1 pr-2.5 transition-colors hover:border-brand-300 hover:bg-surface-2"
              >
                <Avatar
                  size={30}
                  src={user?.profilePhotoUrl || undefined}
                  fallback={<User className="size-[18px]" />}
                  style={{ background: brand.harvest, color: brand.primaryDark }}
                />
                <span className="hidden text-left leading-[1.15] sm:block">
                  <span className="block text-[13px] font-semibold text-content">{user?.fullName}</span>
                  <span className="block text-[10px] uppercase tracking-[0.4px] text-subtle">{user?.role}</span>
                </span>
                <ChevronDown size={14} className="text-subtle" />
              </button>
            </Dropdown>

            {/* Hamburger (mobile only) */}
            <IconButton
              aria-label="Menu"
              className={cn(headerIconBtn, 'xl:hidden')}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X /> : <MenuIcon />}
            </IconButton>
          </div>
        </div>

        {/* Mobile nav sheet */}
        {mobileOpen && (
          <div
            className="absolute inset-x-0 top-full z-40 max-h-[calc(100vh-4rem)] overflow-y-auto border-b border-line bg-surface p-2 shadow-pop animate-pop-in xl:hidden"
            onClick={() => setMobileOpen(false)}
          >
            {nav.map(renderMobileGroup)}
          </div>
        )}
      </header>

      {/* ───────────────── Content — sits on the canvas; cards provide the surfaces ─────────────────
          pb clears the fixed ChatbotWidget FAB (56px @ right:24/bottom:24 → owns the bottom-right ~80px) so
          bottom-right page content (e.g. table pagination) is never hidden behind it. */}
      <main className="min-h-[280px] flex-1 p-4 pb-24 sm:p-6 sm:pb-24">
        {/* Subtle page transition on route change (fade + 8px lift). Keyed on the
            pathname via useGsapPageEnter; children are NOT remounted, so no page
            state is reset. Fully skipped under prefers-reduced-motion. */}
        <div ref={pageRef}>
          {/* Global Back button — on every page except the landing/home. navigate(-1). */}
          {!HIDE_BACK.has(location.pathname) && (
            <div className="mb-4">
              <BackButton />
            </div>
          )}
          <Outlet />
        </div>
      </main>

      <ChatbotWidget />
    </div>
  );
}
